class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', (event) => {
      // Invalidate an older response immediately, including during the debounce.
      FacetFiltersForm.requestId = (FacetFiltersForm.requestId || 0) + 1;
      FacetFiltersForm.controller?.abort();
      this.debouncedOnSubmit(event);
    });
    
    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    const requestId = FacetFiltersForm.requestId = (FacetFiltersForm.requestId || 0) + 1;
    FacetFiltersForm.controller?.abort();
    const controller = FacetFiltersForm.controller = new AbortController();
    const status = document.getElementById('FacetResultsStatus');
    const error = document.getElementById('FacetResultsError');
    if (error) error.hidden = true;
    if (status) status.textContent = status.dataset.loading;
    FacetFiltersForm.setLoading(true);
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      for (const section of FacetFiltersForm.getSections()) {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        const cached = FacetFiltersForm.filterData.find(item => item.url === url);
        let html = cached?.html;
        if (!html) {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Unable to load filtered results');
          html = await response.text();
        }
        if (requestId !== FacetFiltersForm.requestId) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.getElementById('ProductGridContainer') || !parsed.getElementById('ProductCount')) {
          throw new Error('Incomplete filtered results');
        }
        if (!cached) FacetFiltersForm.filterData = [...FacetFiltersForm.filterData.slice(-19), { html, url }];
        // Capture the current focus, not the initiating control: the visitor may
        // have moved on while the response was loading.
        const focus = FacetFiltersForm.captureFocus();
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.restoreFocus(focus);
      }
      FacetFiltersForm.searchParamsPrev = searchParams;
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      if (status) {
        const count = document.getElementById('ProductCount')?.textContent.trim() || '';
        status.textContent = [status.dataset.updated, count].filter(Boolean).join(' ');
      }
    } catch (err) {
      if (requestId !== FacetFiltersForm.requestId) return;
      if (status) status.textContent = '';
      if (error) {
        const retry = error.querySelector('a');
        if (retry) retry.href = window.location.pathname + (searchParams ? '?' + searchParams : '');
        error.hidden = false;
      }
    } finally {
      clearTimeout(timeout);
      if (requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.toggleActiveFacets(false);
      }
    }
  }

  static setLoading(loading) {
    const grid = document.getElementById('ProductGridContainer');
    grid?.setAttribute('aria-busy', String(loading));
    grid?.querySelector('.collection')?.classList.toggle('loading', loading);
    ['ProductCount', 'ProductCountDesktop'].forEach(id => {
      document.getElementById(id)?.classList.toggle('loading', loading);
    });
  }

  static captureFocus() {
    const element = document.activeElement;
    if (!element?.closest('.facets-container')) return null;
    const filter = element.closest('.js-filter');
    return { element, id: element.id, filterIndex: filter?.dataset.index,
      mobile: !!element.closest('#FacetFiltersFormMobile, .active-facets-mobile') };
  }

  static restoreFocus(focus) {
    if (!focus) return;
    if (!focus.element.isConnected) {
      const visible = element => element && element.getClientRects().length && !element.closest('details:not([open]) .mobile-facets__submenu');
      const same = focus.id && document.getElementById(focus.id);
      const filter = focus.filterIndex && document.querySelector(`.js-filter[data-index="${focus.filterIndex}"]`);
      const next = document.querySelector(focus.mobile ? '.active-facets-mobile a' : '.active-facets-desktop a');
      const fallback = document.querySelector(focus.mobile ? '.mobile-facets__open-wrapper' : '#FacetsWrapperDesktop summary, #SortBy');
      [same, filter?.querySelector('summary'), next, fallback].find(visible)?.focus({ preventScroll: true });
    }
    // Rebuild the mobile focus loop after its controls have been replaced.
    const drawer = document.activeElement?.closest('menu-drawer');
    const openDetails = document.activeElement?.closest('details[open]');
    if (drawer && openDetails) {
      const panel = document.activeElement.closest('.mobile-facets__submenu') || openDetails;
      trapFocus(panel, document.activeElement);
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
    // AJAX replacements do not rerun card scripts; show their server-rendered badges.
    document.querySelectorAll('#ProductGridContainer [data-tth-badge-shell]').forEach((badge) => {
      badge.style.visibility = 'visible';
      badge.style.opacity = '1';
    });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const matchesIndex = (element) => { 
      const jsFilter = event?.type === 'input' ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false; 
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // Keep the sort control itself so keyboard focus survives updates.
    ['SortBy', 'SortBy-mobile'].forEach(id => {
      const source = html.getElementById(id);
      const target = document.getElementById(id);
      if (source && target) { target.value = source.value; target.parentElement.querySelector('.tth-sort')?.syncSelection?.(); }
    });
    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
    tthEnhanceSortControls();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    FacetFiltersForm.renderPage(new URL(event.currentTarget.href).searchParams.toString(), event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define('facet-remove', FacetRemove);

// V599: progressively enhance native sort selects; keep their submission contract.
function tthEnhanceSortControls(){
  document.querySelectorAll('#SortBy, #SortBy-mobile').forEach(select=>{
    const host=select.parentElement;if(host.querySelector('.tth-sort'))return;
    const details=document.createElement('details');details.className='tth-sort';
    const summary=document.createElement('summary');summary.setAttribute('aria-label','Sort by');summary.addEventListener('click',event=>event.stopImmediatePropagation());
    const choices=document.createElement('div');choices.className='tth-sort__options';choices.setAttribute('role','group');choices.setAttribute('aria-label','Sort order');
    for(const option of select.options){if(option.hidden||option.disabled)continue;const button=document.createElement('button');button.type='button';button.textContent=option.text;button.dataset.value=option.value;
      button.addEventListener('click',event=>{event.stopImmediatePropagation();const changed=select.value!==option.value;select.value=option.value;details.syncSelection();details.open=false;summary.focus({preventScroll:true});if(changed)select.dispatchEvent(new Event('input',{bubbles:true}));});choices.append(button);}
    details.syncSelection=()=>{summary.textContent=select.selectedOptions[0]?.text||'';summary.setAttribute('aria-label','Sort by: '+summary.textContent);for(const button of choices.children)button.setAttribute('aria-pressed',String(button.dataset.value===select.value));};
    details.append(summary,choices);host.append(details);host.classList.add('tth-sort-enhanced');details.syncSelection();
    details.addEventListener('keydown',event=>{const buttons=[...choices.children],index=buttons.indexOf(document.activeElement);let next;
      if(event.key==='Escape'&&details.open){event.preventDefault();event.stopPropagation();details.open=false;summary.focus();return;}
      if(event.key==='ArrowDown'||event.key==='ArrowUp'){event.preventDefault();details.open=true;next=index<0?buttons.findIndex(b=>b.getAttribute('aria-pressed')==='true'):(index+(event.key==='ArrowDown'?1:-1)+buttons.length)%buttons.length;buttons[Math.max(0,next)]?.focus();}
      if((event.key==='Home'||event.key==='End')&&index>=0){event.preventDefault();buttons[event.key==='Home'?0:buttons.length-1]?.focus();}
    });
    details.addEventListener('focusout',event=>{if(event.relatedTarget&&details.contains(event.relatedTarget))return;setTimeout(()=>{if(!details.contains(document.activeElement))details.open=false;},0);});
    document.addEventListener('click',event=>{if(!details.contains(event.target))details.open=false;});
  });
}
if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',tthEnhanceSortControls,{once:true});else tthEnhanceSortControls();